import { Fragment, RefObject, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency, getCurrency } from '../../../../helpers/currency-helpers';
import { truncateLabel } from '../../../../helpers/string-helpers/string-helpers';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { skyLight } from '../../../../styles/design-tokens';
import { LineChartDot } from '../../../strategy/initiative-performance/components/line-chart/line-chart-dot';
import { DEFAULT_CHART_COLORS } from '../../constants';
import { addTrendLines } from '../allocation-of-costs/allocation-of-costs.helpers';
import { CustomTooltip } from '../shared/custom-tooltip';
import { Legend as CostByInitiativeLegend } from '../shared/Legend';
import { assignColorsToItems, formatXAxisLabel, getItemColor } from '../shared/shared.helpers';
import type { DataPoint } from '../shared/shared.type';
import { formatYAxisTickValue } from './cost-by-initiative.helpers';

function Chart({
  chartData,
  initiativeInfo,
  chartRef,
}: {
  chartData: DataPoint[];
  initiativeInfo: { [id: string]: string };
  chartRef: RefObject<HTMLDivElement>;
}) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { selectedInitiativeIds, selectedTrendLines } = useFinancialsStore((state) => state.filters.costByInitiative);

  const chartDataWithTrendLines = addTrendLines(chartData);
  // Only render initiatives that exist in the current data (have names in initiativeInfo)
  const validInitiativeIds = selectedInitiativeIds.filter((id) => initiativeInfo[id]);
  const colorAssignments = assignColorsToItems(validInitiativeIds, DEFAULT_CHART_COLORS);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  return (
    <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer>
        <LineChart data={chartDataWithTrendLines}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            tickFormatter={formatXAxisLabel}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            padding={{ left: 40, right: 40 }}
          />
          <YAxis
            orientation="left"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(value) => formatYAxisTickValue(value, currencyCode)}
          />
          <Tooltip
            content={
              <CustomTooltip
                filterFn={(item) => Boolean(item.name) && selectedInitiativeIds.includes(item.name!)}
                nameFormatter={(item) => truncateLabel(initiativeInfo[item.name as string] ?? item.name ?? '')}
                valueFormatter={(item) =>
                  formatCurrency(Number(item.value), {
                    currency: currencyCode,
                  })
                }
              />
            }
          />
          {validInitiativeIds.map((initiativeId) => {
            const color = getItemColor(initiativeId, colorAssignments);
            return (
              <Fragment key={initiativeId}>
                <Line
                  type="monotone"
                  dataKey={initiativeId}
                  name={initiativeInfo[initiativeId]}
                  stroke={color}
                  strokeWidth={hoveredItem === null || hoveredItem === initiativeId ? 2 : 1}
                  opacity={hoveredItem === null || hoveredItem === initiativeId ? 1 : 0.3}
                  fill={color}
                  key={initiativeId}
                  dot={
                    hoveredItem === null || hoveredItem === initiativeId ? <LineChartDot color={color} /> : undefined
                  }
                />
                <Line
                  type="monotone"
                  dataKey={`${initiativeId}_trend`}
                  name={`${initiativeInfo[initiativeId]}_trend`}
                  stroke={color}
                  strokeDasharray="5 5"
                  strokeWidth={hoveredItem === null || hoveredItem === initiativeId ? 2 : 1}
                  opacity={hoveredItem === null || hoveredItem === initiativeId ? 1 : 0.3}
                  fill={color}
                  key={`${initiativeId}_trend`}
                  hide={!selectedTrendLines.includes(initiativeId)}
                />
              </Fragment>
            );
          })}
          <Legend
            content={<CostByInitiativeLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />}
            verticalAlign="bottom"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export { Chart };
